import { Card, CardContent, Typography, useTheme } from '@mui/joy';
import { ChartOptions } from 'chart.js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { HorizontalBarChart, ITssDashboardFilters } from 'reports/components';
import { CHART_COLORS } from 'reports/constants';
import { useGetBomsByPart } from 'reports/hooks';
import { IBomsByPart } from 'reports/types';
import { Level, ModeType, Variant } from 'theme/enums';

const DEFAULT_LIMIT = 10;

interface IProps {
  title: string;
  currentYearStart: string;
  currentYearEnd: string;
  previousYearStart: string;
  previousYearEnd: string;
  filters?: ITssDashboardFilters;
  height: number | string;
  limit?: number;
}

export const JobsByPartChart = ({
  title,
  currentYearStart,
  currentYearEnd,
  previousYearStart,
  previousYearEnd,
  filters,
  height,
  limit = DEFAULT_LIMIT,
}: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === ModeType.DARK;
  const previousYearColor = isDarkMode
    ? CHART_COLORS.previousYear.dark
    : CHART_COLORS.previousYear.light;

  const currentYearPayload = useMemo(
    () => ({
      filters: {
        ...filters,
        jobCreatedAtSince: currentYearStart,
        jobCreatedAtUntil: currentYearEnd,
      },
      limit,
    }),
    [currentYearStart, currentYearEnd, filters, limit]
  );

  const previousYearPayload = useMemo(
    () => ({
      filters: {
        ...filters,
        jobCreatedAtSince: previousYearStart,
        jobCreatedAtUntil: previousYearEnd,
      },
      limit,
    }),
    [previousYearStart, previousYearEnd, filters, limit]
  );

  const {
    records: currentYearRecords,
    isLoading: isLoadingCurrentYear,
    isFetching: isFetchingCurrentYear,
  } = useGetBomsByPart(currentYearPayload);
  const {
    records: previousYearRecords,
    isLoading: isLoadingPreviousYear,
    isFetching: isFetchingPreviousYear,
  } = useGetBomsByPart(previousYearPayload);

  const isLoading =
    isLoadingCurrentYear ||
    isLoadingPreviousYear ||
    isFetchingCurrentYear ||
    isFetchingPreviousYear;

  const transformedData = useMemo(() => {
    if (!currentYearRecords?.length && !previousYearRecords?.length) {
      return { datasets: [], labels: [] };
    }

    const partCounts: Record<string, { currentYear: number; previousYear: number }> = {};

    countPartRecords(previousYearRecords, partCounts, 'previousYear');
    countPartRecords(currentYearRecords, partCounts, 'currentYear');

    const sortedParts = Object.entries(partCounts)
      .sort(
        ([, partACounts], [, partBCounts]) =>
          partACounts.currentYear +
          partACounts.previousYear -
          (partBCounts.currentYear + partBCounts.previousYear)
      )
      .reverse()
      .slice(0, limit);

    const labels = sortedParts.map(([partNumber]) => partNumber);
    const previousYearData = sortedParts.map(([, counts]) => counts.previousYear);
    const currentYearData = sortedParts.map(([, counts]) => counts.currentYear);

    return {
      datasets: [
        {
          backgroundColor: previousYearColor,
          data: previousYearData,
          label: t('labelPreviousYearCases'),
        },
        {
          backgroundColor: CHART_COLORS.currentYear,
          data: currentYearData,
          label: t('labelCurrentYearCases'),
        },
      ],
      labels,
    };
  }, [currentYearRecords, limit, previousYearColor, previousYearRecords, t]);

  const chartOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      scales: {
        x: {
          stacked: false,
        },
        y: {
          stacked: false,
        },
      },
    }),
    []
  );

  return (
    <Card variant={Variant.OUTLINED}>
      <CardContent>
        <Typography level={Level.TITLE_MD} sx={{ marginBottom: 2 }}>
          {title}
        </Typography>
        <HorizontalBarChart
          data={transformedData}
          isLoading={isLoading}
          height={height}
          options={chartOptions}
        />
      </CardContent>
    </Card>
  );
};

const countPartRecords = (
  parts: IBomsByPart[],
  counts: Record<string, { currentYear: number; previousYear: number }>,
  key: keyof Record<string, { currentYear: number; previousYear: number }>
) => {
  parts.forEach((part) => {
    const partNumber = part.partNumber;

    if (!counts[partNumber]) {
      counts[partNumber] = { currentYear: 0, previousYear: 0 };
    }

    counts[partNumber][key] += part.count;
  });
};
